import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaFileAlt, 
  FaArrowLeft, 
  FaLightbulb, 
  FaPuzzlePiece, 
  FaUserFriends, 
  FaChartLine, 
  FaLeaf, 
  FaChild, 
  FaSearch, 
  FaGraduationCap, 
  FaShieldAlt, 
  FaSync 
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import style from './AIPolicyAppendix1.module.css';

const AIPolicyAppendix1 = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 }
  };

  return (
    <div className={style.pageContainer}>
      <header className={style.header}>
        <div className={style.headerContent}>
          <Link to="/ai-policy" className={style.backLink}>
            <FaArrowLeft /> Politika Belgesine Dön
          </Link>
          <div className={style.badge}>
            <FaFileAlt className={style.badgeIcon} />
            <span>EK–1: UYGULAMA PLANI</span>
          </div>
          <h1 className={style.title}>Talent14 Yapay Zekâ İlkeleri <br/> Uygulama Planı</h1>
          <p className={style.subtitle}>
            On temel ilkenin Talent14 ekosisteminde nasıl hayata geçirildiğine dair net yanıtlar ve uygulama stratejileri.
          </p>
        </div>
      </header>

      <main className={style.mainContent}>
        <section className={style.introSection}>
          <p>Bu ek doküman, Talent14 Yapay Zekâ Politika Belgesi’nde tanımlanan 10 temel ilkenin her birinin, Talent14 ekosisteminde nasıl hayata geçirildiğini ve <strong>“Bu ilkeyi yapay zekâ ile nasıl çözüyoruz?”</strong> sorusuna verilen net yanıtları ortaya koyar.</p>
          <p>Bu metin; araç listesi veya modül karşılaştırması değildir. Amaç, Talent14’ün yapay zekâyı hangi katmanlarda, hangi pedagojik rolle ve hangi sınırlar içinde kullandığını ilke bazında açıklamaktır.</p>
        </section>

        <section className={style.section}>
          <h2 className={style.sectionTitle}>🧭 İlke Bazlı Uygulama</h2>
          <motion.div 
            className={style.principlesList}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <AppendixCard 
              icon={<FaLightbulb />} 
              tag="1.1" 
              title="Bilinçli Araç Kullanımı ve Sürekli Destek" 
              content="Talent14’te yapay zekâ, yalnızca anlık ihtiyaçlara cevap veren bir sistem değil; kullanıcıya yapay zekâ teknolojisini nasıl doğru kullanacağını öğreten sürekli bir destek mekanizması olarak konumlandırılır. Yapay zekâ; ne zaman, neden ve nasıl kullanılacağına dair yönlendirici geri bildirimler sunar. Bu sayede kullanıcı, yapay zekâdan faydalanırken aynı zamanda onun çalışma mantığını ve sınırlarını öğrenir. Gereksiz, amaç dışı ve bağımlılık yaratan kullanım senaryoları sistemsel olarak azaltılır."
              variants={itemVariants}
            />
            <AppendixCard 
              icon={<FaPuzzlePiece />} 
              tag="1.2" 
              title="Üretim Odaklı Yetkinlik" 
              content="Talent14, yapay zekâyı nihai ürün üreten bir sistem olarak değil; üretimi başlatan, besleyen ve yönlendiren bir keşif ve taslak katmanı olarak kullanır. AI14; metin, görsel, ses, 3D ve video üretimini yaşa ve etik ilkelere uygun biçimde sunarak yaratıcı düşünme ve alternatif senaryo geliştirme becerilerini destekler. Üretilen çıktılar “tamamlanmış ürün” değil, öğrencinin üzerinde düşüneceği ham içerik ve ilham niteliğindedir. CodeAI ise kodu otomatik yazmaz; öğrencinin yazdığı kodu açıklayarak, refactor önerileri sunarak anlayarak üretmesini sağlar."
              variants={itemVariants}
            />
            <AppendixCard 
              icon={<FaUserFriends />} 
              tag="1.3" 
              title="İnsan Merkezlilik" 
              content="Talent14’te yapay zekâ hiçbir zaman pedagojik lider değildir. Öğretmen öğrenme hedeflerini belirler, öğrenci öğrenme sürecini yürütür. Yapay zekâ; açıklayan, alternatif sunan ve rehberlik eden bir yardımcı rol üstlenir. AI Asist, nihai karar vermez; AI Tool değerlendirme yapmaz; AI14 ve CodeAI insan onayı olmadan sonuç üretmez. Bu yapı, tüm kritik kararların ve sorumluluğun insanda kalmasını garanti altına alır."
              variants={itemVariants}
            />
            <AppendixCard 
              icon={<FaChartLine />} 
              tag="1.4" 
              title="Etki Odaklılık" 
              content="Talent14’te yapay zekâ kullanımı, kullanım sıklığı veya çıktı miktarıyla değil; öğrenme sürecine sağladığı gerçek pedagojik katkı ile değerlendirilir. AI Tool’lar yalnızca belirli kazanımlara hizmet eden senaryolarda çalışır. AI Asist, gereksiz etkileşimi azaltacak şekilde bağlamsal cevap üretir. Bu sayede yapay zekâ, dikkat dağıtan bir unsur değil; öğrenme etkisini artıran bir destek mekanizması hâline gelir."
              variants={itemVariants}
            />
            <AppendixCard 
              icon={<FaLeaf />} 
              tag="1.5" 
              title="Kaynak Farkındalığı" 
              content="Talent14, yapay zekâyı yalnızca dijital bir araç olarak değil; su, enerji ve hesaplama gücü gibi fiziksel kaynaklara dayanan bir teknoloji olarak ele alır. Yapay zekânın arkasındaki enerji tüketimi ve çevresel etkiler kullanıcıya dolaylı olarak hissettirilir. Kredi, süre ve bağlam sınırları; yapay zekânın sınırsız bir kaynak olmadığı bilincini oluşturur. Bu yaklaşım, yapay zekânın yakıtının enerji ve doğal kaynaklar olduğu gerçeğini görünür kılar."
              variants={itemVariants}
            />
            <AppendixCard 
              icon={<FaChild />} 
              tag="1.6" 
              title="Yaş Duyarlılığı" 
              content="Talent14, çocukların ve gençlerin bilişsel, duygusal ve etik gelişim düzeylerini yapay zekâ kullanımının merkezine alır. Küçük yaş gruplarında yapay zekâ; sınırları net, yönlendirici ve koruyucu bir yapıda sunulur. Üst yaş gruplarında ise eleştirel düşünme, sorgulama ve üretim becerilerini destekleyen rehber bir role evrilir. Bu yapı, çocukların yapay zekâya maruz kalan değil; onu güvenli biçimde tanıyan ve anlamlandıran bireyler olarak gelişmesini hedefler."
              variants={itemVariants}
            />
            <AppendixCard 
              icon={<FaSearch />} 
              tag="1.7" 
              title="Tam Şeffaflık" 
              content="Talent14’te yapay zekâ sistemleri tamamen görünür, izlenebilir ve denetlenebilir şekilde çalışır. Kullanıcı, yapay zekânın nerede devrede olduğunu bilir; kullanım kayıtları güvenlik ve kalite amaçlı tutulur. Olası güvenlik ihlalleri, riskli senaryolar ve hatalı kullanımlar sistemsel olarak izlenir ve yönetilir. Şeffaflık, yalnızca bilgilendirme değil; aktif güvenlik yönetiminin bir parçasıdır."
              variants={itemVariants}
            />
            <AppendixCard 
              icon={<FaGraduationCap />} 
              tag="1.8" 
              title="Öğrenme Önceliği" 
              content="Talent14’te yapay zekâ yalnızca öğrenmeye destek olmaz; aynı zamanda yapay zekânın kendisinin doğru öğretilmesini ve doğru öğrenilmesini sağlar. Kullanıcılar, yapay zekâyı nasıl sorgulayacaklarını, çıktıları nasıl değerlendireceklerini ve nerede durmaları gerektiğini öğrenir. Yapay zekâ; öğretmenin pedagojik rehberliğiyle birlikte, öğrenme sürecinin konusu ve aracı olarak yapılandırılır."
              variants={itemVariants}
            />
            <AppendixCard 
              icon={<FaShieldAlt />} 
              tag="1.9" 
              title="Etik Güvence" 
              content="Talent14’te etik güvence; yalnızca kural setlerinden ibaret değildir. Güvenlik ihlalleri, etik riskler ve uygunsuz kullanım senaryoları sistemsel olarak tespit edilir, izlenir ve yönetilir. Yapay zekâ katmanları; yaş hassasiyeti, içerik güvenliği ve pedagojik uygunluk filtreleriyle korunur. Etik; pasif bir ilke değil, aktif olarak işletilen bir güvenlik sürecidir."
              variants={itemVariants}
            />
            <AppendixCard 
              icon={<FaSync />} 
              tag="1.10" 
              title="Yaşayan Politika" 
              content="Talent14 Yapay Zekâ Politikası, sabit ve değişmez bir metin olarak ele alınmaz. Eğitim yaklaşımları, teknolojik gelişmeler, etik standartlar ve toplumsal beklentiler doğrultusunda bu politika sürekli gözden geçirilir ve güncellenir. Kullanıcı geri bildirimleri, kurumsal ihtiyaçlar ve yeni risk alanları politikanın evrilmesini sağlar. Bu yaklaşım, Talent14’ün yapay zekâ yönetişimini yaşayan, gelişen ve kendini yenileyen bir yapı hâline getirir."
              variants={itemVariants}
            />
          </motion.div>
        </section>
      </main>

      <footer className={style.pageFooter}>
        <div className={style.footerContent}>
          <p>© {new Date().getFullYear()} Talent14 - Güvenli Yapay Zeka Önceliği</p>
        </div>
      </footer>
    </div>
  );
};

const AppendixCard = ({ icon, tag, title, content, variants }) => (
  <motion.div 
    variants={variants}
    className={style.appendixCard}
    whileHover={{ x: 10 }}
  >
    <div className={style.cardHeader}>
      <div className={style.cardIcon}>{icon}</div>
      <div className={style.cardTitleArea}>
        <span className={style.cardTag}>{tag}</span>
        <h3>{title}</h3>
      </div>
    </div>
    <div className={style.cardBody}>
      <div className={style.solutionBadge}>Nasıl Çözüyoruz?</div>
      <p>{content}</p>
    </div>
  </motion.div>
);

export default AIPolicyAppendix1;
